import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Public as PublishIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  MenuBook as GuideIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

const travelStyles = [
  'budget', 'mid-range', 'luxury', 'backpacker', 
  'family', 'adventure', 'cultural', 'relaxation'
];

const PublishGuideDialog = ({ open, onClose, trip, tripId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(trip?.title || '');
  const [description, setDescription] = useState(trip?.description || '');
  const [destination, setDestination] = useState('');
  const [travelStyle, setTravelStyle] = useState('mid-range');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim() && tags.length < 8) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handlePublish = async () => {
    if (!title.trim()) {
      setError(t('publishGuide.titleRequired'));
      return;
    }

    setPublishing(true);
    setError('');

    try {
      const response = await api.post('/guides/publish', {
        tripId,
        title: title.trim(),
        description: description.trim(),
        destination: destination.trim(),
        travelStyle,
        tags: tags.length > 0 ? tags : undefined,
        isAnonymous,
      });

      if (response.data.success) {
        setSuccess(response.data);
      }
    } catch (err) {
      console.error('Publish error:', err);
      if (err.response?.data?.existingSlug) {
        setError(t('publishGuide.alreadyPublished'));
      } else {
        setError(err.response?.data?.error || t('publishGuide.error'));
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleViewGuide = () => {
    if (success?.guide?.slug) {
      navigate(`/guides/${success.guide.slug}`);
    }
    onClose();
  };

  const handleClose = () => {
    setError('');
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GuideIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('publishGuide.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('publishGuide.published')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('publishGuide.publishedDescription')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleViewGuide}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {t('publishGuide.viewGuide')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('publishGuide.description')}
            </Typography>

            <TextField
              label={t('publishGuide.guideTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              label={t('publishGuide.guideDescription')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              label={t('publishGuide.destination')}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              fullWidth
              placeholder={t('publishGuide.destinationPlaceholder')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <FormControl fullWidth>
              <InputLabel>{t('publishGuide.travelStyle')}</InputLabel>
              <Select
                value={travelStyle}
                onChange={(e) => setTravelStyle(e.target.value)}
                label={t('publishGuide.travelStyle')}
                sx={{ borderRadius: 2 }}
              >
                {travelStyles.map(style => (
                  <MenuItem key={style} value={style}>
                    {t(`guides.style.${style}`, style.charAt(0).toUpperCase() + style.slice(1).replace('-', ' '))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <TextField
                label={t('publishGuide.addTags')}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                fullWidth
                placeholder={t('publishGuide.tagsPlaceholder')}
                helperText={t('publishGuide.tagsHelper')}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => handleRemoveTag(tag)}
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('publishGuide.publishAnonymously')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('publishGuide.anonymousHint')}
                  </Typography>
                </Box>
              }
              sx={{ mt: 1, ml: 0 }}
            />
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />}
            onClick={handlePublish}
            disabled={publishing || !title.trim()}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {publishing ? t('publishGuide.publishing') : t('publishGuide.publish')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PublishGuideDialog;
